import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useForm, Controller } from "react-hook-form";
import { ErrorMessage } from "@hookform/error-message";
import classNames from "classnames";
import Select from "react-select";
import { Form, Row, Col, Alert } from "react-bootstrap";
import { useIntl } from "react-intl";
import { useDispatch, useSelector } from "react-redux";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";
import isEmpty from "lodash/isEmpty";
import SVG from "react-inlinesvg";
import { Card, CardBody } from "../../../_metronic/_partials/controls";
import { decryptedData, getDemoMode } from "../../../helpers";
import {
  addTransaction,
  getActiveCamapignList,
  getInvestorList,
} from "../../../store/actions/transactions/transactionsActions";
import { formatCurrency } from "../../../helpers/numberFormat";
import { toAbsoluteUrl } from "../../../_metronic/_helpers";
import "./AddTransaction.scss";

const AddTransaction = () => {
  const navigate = useNavigate();
  const intl = useIntl();
  const dispatch = useDispatch();

  const demoMode = getDemoMode();

  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [campaignSelectOptions, setCampaignSelectOptions] = useState([]);
  const [investorsSelectOptions, setInvestorsSelectOptions] = useState([]);
  const [numberOfShares, setNumberOfShares] = useState(0);
  const isEncryption = useRef(
    import.meta.env.VITE_DATABASE_ENCRYPTION === "true"
  );

  useEffect(() => {
    dispatch(getActiveCamapignList());
    dispatch(getInvestorList());
  }, [dispatch]);

  const { activeCamapigns, investors, successAlert, isSpinning, error } =
    useSelector((state) => state.transaction);
  const { generalSettings } = useSelector((state) => state.settings);
  const { taxonomy } = useSelector((state) => state?.taxonomy);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
    control,
    setValue,
  } = useForm({ mode: "onChange" });

  const backToList = () => {
    navigate(`/transaction/`);
  };

  useEffect(() => {
    const camapignList = activeCamapigns?.map(
      ({ companyName, _id: value }) => ({
        label: isEncryption.current ? decryptedData(companyName) : companyName,
        value,
      })
    );
    setCampaignSelectOptions(camapignList);
  }, [activeCamapigns]);

  useEffect(() => {
    const investorList = investors?.map(({ fullName: label, _id: value }) => ({
      label: isEncryption.current
        ? decryptedData(label.split(" ")[0]) +
          " " +
          decryptedData(label.split(" ")[1])
        : label,
      value,
    }));
    setInvestorsSelectOptions(investorList);
  }, [investors]);

  const oncampaignIdSelect = (selectedCampaignId) => {
    setSelectedCampaign(
      activeCamapigns?.filter(
        (campaign) => campaign._id === selectedCampaignId
      )?.[0]
    );
  };
  const MySwal = withReactContent(Swal);
  const alertBox = (title, text, icon) => {
    MySwal.fire({
      title: title,
      text: text,
      icon: icon,
      allowOutsideClick: false,
      allowEscapeKey: false,
    }).then((result) => {
      if (result["isConfirmed"]) {
        navigate(`/transaction/`);
      }
    });
  };

  useEffect(() => {
    if (successAlert) {
      alertBox(
        intl.formatMessage({ id: "transaction.addTransaction.created" }),
        intl.formatMessage({
          id: "transaction.addTransaction.created.message",
        }),
        "success"
      );
    }
     
  }, [successAlert]);

  const onSubmitAddTransaction = (data) => {
    data.createdAt = Date.now();
    dispatch(addTransaction(data));
    reset();
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <>
      <Card>
        <div className="page-heading-text pt-5 px-5">
          <button className="go-back-btn" onClick={backToList}>
            <i className="fa fa-chevron-left"></i>
          </button>
          <span>
            {intl.formatMessage({
              id: "transaction.addTransaction.title",
            })}
          </span>
          {selectedCampaign ? (
            <p className="rounded-fix info-bg-clr text-info font-xl font-size-sm mb-0 fw-medium ms-auto py-2 px-3">
              Project Type:{" "}
              <span>
                {selectedCampaign?.termsSlug === "equity"
                  ? "EQUITY"
                  : selectedCampaign?.termsSlug === "debt"
                  ? "DEBT"
                  : selectedCampaign?.termsSlug === "equityWithDividend"
                  ? "EQUITY + DIVIDEND"
                  : ""}
              </span>
            </p>
          ) : null}
        </div>

        <CardBody className="px-5">
          <Row className="justify-content-md-center">
            <Col md={12}>
              {!isEmpty(error) ? <Alert variant="danger">{error}</Alert> : null}
              <Form
                id="AddTransactionForm"
                className="form form-label-right"
                onSubmit={handleSubmit(onSubmitAddTransaction)}>
                <div className="d-flex flex-column flex-lg-row gap-3 gap-md-5">
                  <Form.Group className="mb-4 w-50 floating-input-field-wrapper">
                    <Form.Label className="floating-input-field-label-static ms-4 start-0">
                      {intl.formatMessage(
                        { id: "transaction.addTransaction.activeCampaigns" },
                        { value: taxonomy?.projectPlural }
                      )}
                      <span className="text-important"> *</span>
                    </Form.Label>
                    <Controller
                      name={"campaignId"}
                      control={control}
                      rules={{
                        required: intl.formatMessage({
                          id: "COMMON.FIELD.REQ",
                        }),
                      }}
                      render={({ field: { value, onChange } }) => {
                        return (
                          <Select
                            placeholder={intl.formatMessage(
                              {
                                id: "transaction.addTransaction.selectCampaign",
                              },
                              { value: taxonomy?.projectSingular }
                            )}
                            classNamePrefix="react-select"
                            className={classNames(
                              "react-select-container text-black",
                              {
                                "is-invalid": errors?.campaignId,
                              }
                            )}
                            inputId="addTransactionCampaignId"
                            options={campaignSelectOptions}
                            value={campaignSelectOptions?.find(
                              (c) => c.value === value
                            )}
                            onChange={(val) => {
                              reset({ campaignId: val.value });
                              oncampaignIdSelect(val.value);
                              onChange(val.value);
                            }}
                          />
                        );
                      }}
                    />
                    <ErrorMessage
                      errors={errors}
                      name="campaignId"
                      render={({ message }) => (
                        <div
                          className="invalid-feedback"
                          id="addTransactionCampaignIdErr">
                          {message}
                        </div>
                      )}
                    />
                  </Form.Group>
                  <Form.Group className="mb-4 w-50 floating-input-field-wrapper">
                    <Form.Label className="floating-input-field-label-static ms-4 start-0">
                      {intl.formatMessage({
                        id: "transaction.addTransaction.investorDetail",
                      })}
                      <span className="text-important"> *</span>
                    </Form.Label>
                    <Controller
                      name={"user"}
                      control={control}
                      rules={{
                        required: intl.formatMessage({
                          id: "COMMON.FIELD.REQ",
                        }),
                      }}
                      render={({ field: { value, onChange } }) => {
                        return (
                          <Select
                            placeholder={intl.formatMessage({
                              id: "transaction.addTransaction.selectUser",
                            })}
                            className={classNames(
                              "react-select-container text-black",
                              {
                                "is-invalid": errors?.user,
                              }
                            )}
                            inputId="addTransactionCampaignId"
                            classNamePrefix="react-select"
                            options={investorsSelectOptions}
                            value={
                              investorsSelectOptions?.find(
                                (c) => c.value === value
                              ) || ""
                            }
                            onChange={(val) => {
                              onChange(val.value);
                            }}
                          />
                        );
                      }}
                    />
                    <ErrorMessage
                      errors={errors}
                      name="user"
                      render={({ message }) => (
                        <div
                          className="invalid-feedback"
                          id="addTransactionCampaignIdErr">
                          {message}
                        </div>
                      )}
                    />
                  </Form.Group>
                </div>
                {selectedCampaign ? (
                  <div className="bg-table-bg-clr p-4 rounded">
                    <Row className="gap-6 gap-xl-0">
                      <Col xl={4}>
                        <Card>
                          <div className="d-flex align-items-center gap-2 label-light-success p-3">
                            <SVG
                              className="me-1 flex-shrink-0"
                              height={36}
                              width={36}
                              src={toAbsoluteUrl(
                                "media/svg/icons/General/goal-icon.svg"
                              )}
                            />
                            <p className="text-dark fw-normal d-flex text-start flex-column mb-0">
                              {intl.formatMessage({
                                id: "transaction.addTransaction.goal",
                              })}
                              <strong className="text-black font-size-h5">
                                {formatCurrency(
                                  selectedCampaign?.goal ?? 0,
                                  generalSettings.currencySymbolSide,
                                  selectedCampaign.equityCurrencySymbol ?? "",
                                  selectedCampaign?.equityCurrencyCode ?? "",
                                  generalSettings?.decimalPoints
                                )}
                              </strong>
                            </p>
                          </div>
                        </Card>
                      </Col>
                      <Col xl={4}>
                        <Card>
                          <div className="d-flex align-items-center gap-2 label-light-successful p-3">
                            <i
                              style={{ transform: "rotate(90deg)" }}
                              className="fa-solid fa-play font-size-h3 me-2"></i>
                            <p className="text-dark fw-normal d-flex flex-column mb-0">
                              {intl.formatMessage({
                                id: "transaction.addTransaction.minimumRaise",
                              })}
                              <strong className="text-black font-size-h5">
                                {formatCurrency(
                                  selectedCampaign?.minimumRaise ?? 0,
                                  generalSettings.currencySymbolSide,
                                  selectedCampaign.equityCurrencySymbol ?? "",
                                  selectedCampaign?.equityCurrencyCode ?? "",
                                  generalSettings?.decimalPoints
                                )}
                              </strong>
                            </p>
                          </div>
                        </Card>
                      </Col>
                      <Col xl={4}>
                        <Card>
                          <div className="d-flex align-items-center gap-2 label-light-successful p-3">
                            <i
                              style={{ transform: "rotate(270deg)" }}
                              className="fa-solid fa-play font-size-h3 me-2"></i>
                            <p className="text-dark fw-normal d-flex flex-column mb-0">
                              {intl.formatMessage({
                                id: "transaction.addTransaction.maximumRaise",
                              })}
                              <strong className="text-black font-size-h5">
                                {formatCurrency(
                                  selectedCampaign?.maximumRaise ?? 0,
                                  generalSettings.currencySymbolSide,
                                  selectedCampaign.equityCurrencySymbol ?? "",
                                  selectedCampaign?.equityCurrencyCode ?? "",
                                  generalSettings?.decimalPoints
                                )}
                              </strong>
                            </p>
                          </div>
                        </Card>
                      </Col>
                    </Row>

                    {selectedCampaign?.termsSlug === "equity" ? (
                      <>
                        <Row>
                          <Col>
                            <Card className="info-bg-clr text-info fs-6 fw-bold text-center py-0 mt-5">
                              <span className="m-2">
                                {intl.formatMessage({
                                  id: "transaction.addTransaction.availableShare",
                                })}
                                :{" "}
                                <b>
                                  {selectedCampaign?.availableShares -
                                    numberOfShares}
                                </b>
                              </span>
                            </Card>
                          </Col>
                        </Row>
                        <div className="equity-shares-field-wrapper d-flex align-items-center justify-content-between justify-content-center mt-5">
                          <Card className="equity-shares-field-box border bg-transparent border-primary text-center">
                            <div className="m-3">
                              <p className="text-dark mb-0">
                                {intl.formatMessage({
                                  id: "transaction.addTransaction.shareUnits",
                                })}
                              </p>
                              <strong className="text-black fs-5">
                                {numberOfShares}
                              </strong>
                            </div>
                          </Card>

                          <h5 className="equity-shares-field-sign text-center fs-6 text-center text-black mb-0 rounded-circle">
                            x
                          </h5>
                          <Card className="equity-shares-field-box border bg-transparent border-primary text-center">
                            <div className="m-3">
                              <p className="text-dark mb-0">
                                {intl.formatMessage({
                                  id: "transaction.addTransaction.shareUnitPrice",
                                })}
                              </p>
                              <strong className="text-black fs-5">
                                {formatCurrency(
                                  selectedCampaign?.pricePerShare ?? 0,
                                  generalSettings.currencySymbolSide,
                                  selectedCampaign.equityCurrencySymbol ?? "",
                                  selectedCampaign?.equityCurrencyCode ?? "",
                                  generalSettings?.decimalPoints
                                )}
                              </strong>
                            </div>
                          </Card>

                          <h5 className="equity-shares-field-sign text-center fs-6 text-center text-black mb-0 rounded-circle">
                            =
                          </h5>

                          <Card className="equity-shares-field-box border border-primary bg-transparent text-center">
                            <div className="m-3">
                              <p className="text-dark mb-0">
                                {intl.formatMessage({
                                  id: "transaction.addTransaction.totalInvestment",
                                })}
                              </p>
                              <strong className="text-black fs-5">
                                {formatCurrency(
                                  selectedCampaign?.pricePerShare *
                                    numberOfShares,
                                  generalSettings.currencySymbolSide,
                                  selectedCampaign.equityCurrencySymbol ?? "",
                                  selectedCampaign?.equityCurrencyCode ?? "",
                                  generalSettings?.decimalPoints
                                )}
                              </strong>
                            </div>
                          </Card>
                        </div>
                      </>
                    ) : null}
                    <div className="d-flex align-items-start mt-5 gap-5">
                      {selectedCampaign?.termsSlug === "equity" ? (
                        <Form.Group className="w-50 mb-4 floating-input-field-wrapper">
                          <Form.Label className="floating-input-field-label-static start-0 ms-4">
                            {intl.formatMessage({
                              id: "transaction.addTransaction.numberOfShares",
                            })}
                            <span className="text-important"> *</span>
                          </Form.Label>
                          <Form.Control
                            name="purchasedShares"
                            id="addTransactionPurchasedShares"
                            className={classNames(
                              "add-transaction-input-fields form-control floating-input-field-item bg-white",
                              {
                                "is-invalid": errors.purchasedShares,
                              }
                            )}
                            {...register("purchasedShares", {
                              required: intl.formatMessage({
                                id: "COMMON.FIELD.REQ",
                              }),
                              pattern: {
                                value: /^[0-9]*$/,
                                message: intl.formatMessage({
                                  id: "ONLY.NUMBER",
                                }),
                              },
                              min: {
                                value: 1,
                                message: `${intl.formatMessage({
                                  id: "transaction.addTransaction.minShare",
                                })} ${1}`,
                              },
                              max: {
                                value: selectedCampaign?.availableShares,
                                message: `${intl.formatMessage({
                                  id: "transaction.addTransaction.maxShare",
                                })} ${selectedCampaign?.availableShares}`,
                              },
                              validate: {
                                maxInvestment: (v) =>
                                  parseInt(v) *
                                    selectedCampaign?.pricePerShare <=
                                    selectedCampaign?.maximumRaise ||
                                  `${intl.formatMessage({
                                    id: "transaction.addTransaction.maxAmount",
                                  })} ${selectedCampaign?.maximumRaise}`,
                              },
                              onChange: (e) => {
                                setValue(
                                  "amount",
                                  parseInt(e.target.value) *
                                    selectedCampaign?.pricePerShare
                                );
                                setNumberOfShares(
                                  parseInt(e.target.value) || 0
                                );
                              },
                            })}
                          />
                          <ErrorMessage
                            errors={errors}
                            name="purchasedShares"
                            render={({ message }) => (
                              <div
                                className="invalid-feedback"
                                id="addTransactionPurchasedSharesErr">
                                {message}
                              </div>
                            )}
                          />
                        </Form.Group>
                      ) : (
                        <Form.Group className="w-50 mb-4 floating-input-field-wrapper">
                          <Form.Label className="floating-input-field-label-static ms-4 start-0">
                            {intl.formatMessage({
                              id: "transaction.addTransaction.amount",
                            })}
                            <span className="text-important"> *</span>
                          </Form.Label>
                          <Form.Control
                            name="amount"
                            id="addTransactionAmount"
                            className={classNames(
                              "add-transaction-input-fields form-control bg-white floating-input-field-item",
                              {
                                "is-invalid": errors.amount,
                              }
                            )}
                            {...register("amount", {
                              required: intl.formatMessage({
                                id: "COMMON.FIELD.REQ",
                              }),
                              pattern: {
                                value: /^[0-9]*$/,
                                message: intl.formatMessage({
                                  id: "ONLY.NUMBER",
                                }),
                              },
                              min: {
                                value: 1,
                                message: `${intl.formatMessage({
                                  id: "transaction.addTransaction.minAmount",
                                })} 1`,
                              },
                              max: {
                                value: selectedCampaign?.maximumRaise,
                                message: `${intl.formatMessage({
                                  id: "transaction.addTransaction.maxAmount",
                                })} ${selectedCampaign?.maximumRaise}`,
                              },
                            })}
                          />
                          <ErrorMessage
                            errors={errors}
                            name="amount"
                            render={({ message }) => (
                              <div
                                className="invalid-feedback"
                                id="addTransactionAmountErr">
                                {message}
                              </div>
                            )}
                          />
                        </Form.Group>
                      )}
                      <Form.Group className="mb-4 w-50 bg-white floating-input-field-wrapper-textarea pt-3">
                        <Form.Label className="floating-input-field-label-static bg-white w-100 px-4 start-0 mt-0">
                          {intl.formatMessage({
                            id: "transaction.addTransaction.comments",
                          })}
                        </Form.Label>
                        <Form.Control
                          name="note"
                          as="textarea"
                          rows="1"
                          id="addTransactionNote"
                          className={classNames(
                            "form-control floating-input-field-item pt-3 bg-transparent",
                            {
                              "is-invalid": errors.note,
                            }
                          )}
                          {...register("note")}
                        />
                        <ErrorMessage
                          errors={errors}
                          name="note"
                          render={({ message }) => (
                            <div
                              className="invalid-feedback"
                              id="addTransactionNoteErr">
                              {message}
                            </div>
                          )}
                        />
                      </Form.Group>
                    </div>
                    {demoMode === "false" ? (
                      <button
                        type="submit"
                        id="addTransactionSubmit"
                        className="btn btn-secondary common-save-btn mb-2 mt-1"
                        disabled={isSpinning}>
                        {isSpinning ? (
                          <div
                            className="spinner-border spinner-border-sm"
                            role="status">
                            <span className="sr-only" />
                          </div>
                        ) : (
                          <span>{intl.formatMessage({ id: "SAVE" })}</span>
                        )}
                      </button>
                    ) : null}
                  </div>
                ) : null}
              </Form>
            </Col>
          </Row>
        </CardBody>
      </Card>
    </>
  );
};

export default AddTransaction;
